import { motion } from "framer-motion";
import { Award, BadgeCheck, Mic, ShieldCheck, Wallet, Wand2 } from "lucide-react";

const capabilities = [
  "نمتلك وندير منصات رقمية حيّة من الفكرة حتى الإطلاق",
  "نبني أنظمة مصرفية ومالية وبوابات دفع بمعايير مؤسسية",
  "نصمم بنية تحتية للشبكات والاتصالات",
  "نطبّق امتثال PCI DSS و PDPL لحماية البيانات",
];

const platformPoints = [
  { icon: Wand2, text: "توليد استراتيجيات تسويق كاملة ومحتوى احترافي لكل منصة تواصل" },
  { icon: Mic, text: "مولّد فيديو ذكي بتعليق صوتي باللهجة السعودية واستنساخ أصوات" },
  { icon: Wallet, text: "اشتراكات ومحفظة وبوابة دفع PayTabs وفواتير بالبريد — مدفوعات حقيقية مفعّلة" },
  { icon: ShieldCheck, text: "لوحة إدارة وكوبونات وسياسات قانونية وأمنية كاملة" },
];

export const Experience = () => (
  <section id="experience" data-testid="experience-section" className="relative py-24 sm:py-32 overflow-hidden">
    <div className="absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full blur-[160px] opacity-15 bg-[#003366]" aria-hidden="true" />
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.7 }}
        className="mb-14"
      >
        <div className="font-mono-tech text-xs sm:text-sm tracking-[0.25em] text-[#D4AF37] mb-4" dir="ltr">— OUR EXPERIENCE</div>
        <h2 className="font-display font-extrabold text-2xl sm:text-3xl lg:text-4xl text-[#F8FAFC]" data-testid="experience-title">
          خبرتنا <span className="gold-text">+18 عاماً في الأنظمة المؤسسية</span>
        </h2>
        <p className="mt-4 max-w-2xl font-body text-base sm:text-lg text-[#94A3B8]" data-testid="experience-bio">
          خبرة تمتد لأكثر من 18 عاماً في الأنظمة المؤسسية والمالية لدى البنوك والقطاع المالي،
          والشبكات والاتصالات، وإدارة المشاريع الاستراتيجية — نوظّفها اليوم مع أحدث أدوات
          الذكاء الاصطناعي لنبني منصات ومواقع حقيقية تعمل وتُدرّ دخلاً.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
        <motion.div
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-5"
        >
          <ul className="space-y-4">
            {capabilities.map((c) => (
              <li key={c} className="flex items-center gap-3 font-body text-base text-[#CBD5E1]">
                <BadgeCheck size={20} className="text-[#D4AF37] shrink-0" />
                {c}
              </li>
            ))}
          </ul>
          <div className="mt-8 inline-flex items-center gap-3 num-badge rounded-2xl px-5 py-3" data-testid="experience-badge">
            <Award size={20} className="text-[#E5C158]" />
            <div>
              <span className="font-display font-black text-2xl gold-text">+18</span>
              <span className="font-body text-sm text-[#94A3B8] mr-2">عاماً خبرة مؤسسية ومالية</span>
            </div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="lg:col-span-7"
        >
          <div className="glass-card rounded-3xl p-8 sm:p-10" data-testid="experience-platform-card">
            <div className="flex items-center gap-3 mb-2">
              <span className="num-badge rounded-full px-4 py-1.5 text-xs text-[#E5C158]">منصة حيّة تعمل الآن</span>
            </div>
            <h3 className="font-display font-black text-2xl sm:text-3xl text-[#F8FAFC]">
              «رواج الذكية» — <span className="gold-text">من تطويرنا وتشغيلنا</span>
            </h3>
            <p className="mt-4 font-body text-base leading-relaxed text-[#94A3B8]">
              منصة تسويق ذكية للمنشآت الصغيرة والمتوسطة في السعودية، بنيناها في براح الرقمية
              بأدوار كاملة: ملكية المنتج، تصميم التجربة، وقيادة التنفيذ من الفكرة حتى الإطلاق —
              وتعمل اليوم بمدفوعات واشتراكات حقيقية.
            </p>
            <div className="mt-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {platformPoints.map((p) => (
                <div key={p.text} className="flex items-start gap-3 rounded-2xl bg-[#0F1A36]/60 border border-[#D4AF37]/10 px-4 py-4">
                  <span className="w-9 h-9 rounded-xl bg-[#D4AF37]/15 border border-[#D4AF37]/30 flex items-center justify-center shrink-0">
                    <p.icon size={16} className="text-[#E5C158]" />
                  </span>
                  <span className="font-body text-sm leading-relaxed text-[#CBD5E1]">{p.text}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  </section>
);
